<template>
  <div class="search-patient-modal">
    <b-modal
      :id="idModal"
      ref="bModal"
      size="full"
      modal-class="search-patient-modal"
      hide-footer
      centered
      qa="search-patient-modal"
      @ok="onOk"
      @hide="onHide"
    >
      <template #modal-title>
        <i
          class="fal fa-users mr-2"
          aria-hidden="true"
        />{{ $t('component.patient.searcher.title') }}
      </template>
      <b-container
        fluid
        class="p-2"
      >
        <b-row class="d-flex justify-content-between">
          <b-col>
            <span class="dark">{{ $tc('component.patient.searcher.description') }}:</span>
          </b-col>
        </b-row>
        <patient-searcher-component
          ref="patientSearcher"
          qa-id="search-patient-modal"
          class="my-3"
          :searcher="searcher"
          @search-status="onSearchStatusChanged($event)"
          @search-loading="onSearchLoadingChanged($event)"
          @searched="onSearched($event)"
        />
      </b-container>
      <div>
        <hr style="margin:0">
      </div>
      <div
        v-if="loading"
        class="loading-state"
      >
        <vt-loading />
      </div>
      <div
        v-if="!loading"
        class="patient-container justify-content-center"
      >
        <div
          v-if="noSearchDoneYet || tooManyResults || !rowData.length"
          class="empty-search d-flex align-items-center justify-content-center p-4"
        >
          <div class="w-100">
            <b-row>
              <b-col>
                <div class="d-flex flex-column text-center">
                  <template v-if="noSearchDoneYet">
                    <div>
                      <i
                        class="fal fa-th-list fa-2x mb-2"
                        aria-hidden="true"
                      />
                      <i
                        class="far fa-search ml-1"
                        aria-hidden="true"
                      />
                    </div>
                    <span style="font-size: 16px; margin-top: 16px;">
                      {{ $t('component.patient.searcher.emptyStateText') }}
                    </span>
                  </template>
                  <template v-else-if="searchMessage">
                    <div>
                      <i
                        class="error fal fa-exclamation-circle fa-2x mb-2"
                        aria-hidden="true"
                      />
                    </div>
                    <span
                      class="error"
                      style="font-size: 16px; margin-top: 16px;"
                    >
                      {{ searchMessage }}
                    </span>
                  </template>
                </div>
              </b-col>
            </b-row>
            <b-row
              v-if="$_canCreatePatient() || $_canMergePatient()"
              class="mt-5"
            >
              <b-col class="text-center">
                <span
                  style="font-size: 22px;"
                  class="dark"
                >{{ $t('component.patient.searcher.emptyStateQuestion') }}</span>
              </b-col>
            </b-row>
            <b-row class="d-flex justify-content-center mt-3">
              <b-col
                v-if="$_canMergePatient()"
                lg="3"
                cols="4"
              >
                <b-button
                  v-testing="'patient-merge'"
                  block
                  class="btn__outline"
                  @click="openModalPatientFusion()"
                >
                  <i
                    class="far fa-users-medical mr-2"
                    aria-hidden="true"
                  />
                  {{ $t('component.patient.searcher.patientMerge') }}
                </b-button>
              </b-col>
              <b-col
                v-if="$_canCreatePatient()"
                lg="3"
                cols="4"
              >
                <b-button
                  v-testing="'create-patient'"
                  block
                  class="btn__outline"
                  @click="showCreatePatientModal"
                >
                  <i
                    class="far fa-user-plus mr-2"
                    aria-hidden="true"
                  />
                  {{ $t('component.patient.searcher.newPatient') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
        <div
          v-if="rowData.length > 0"
          class="grid-container mt-2 mb-0"
        >
          <grid-header :gridtitle="`${$t('global.patients')} (${rowData.length})`">
            <div class="d-flex align-items-center">
              <b-button
                v-if="$_canMergePatient()"
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-patient-merge-button"
                :disabled="disabledModalPatientFusionButton"
                @click="openModalPatientFusion(selectedRows[0], selectedRows[1])"
              >
                <i
                  v-tooltip="$t('component.patient.searcher.patientMerge')"
                  class="far fa-users-medical"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="$_canCreatePatient()"
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-create-patient-button"
                @click="showCreatePatientModal"
              >
                <i
                  v-tooltip="$t('global.newPatient')"
                  class="far fa-plus"
                  aria-hidden="true"
                />
              </b-button>
              <div class="vertical-line" />
              <b-button
                v-if="$_canDeletePatient()"
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-delete-patient-button"
                :disabled="selectedRows.length !== 1"
                @click="deletePatient(selectedRows[0].patId)"
              >
                <i
                  v-tooltip="$tc('component.patient.searcher.deletePatient',2)"
                  class="far fa-trash-alt"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="$_canEditPatient()"
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-edit-patient-button"
                :disabled="selectedRows.length !== 1"
                @click="showEditPatientModal(selectedRows[0].patId)"
              >
                <i
                  v-tooltip="$t('component.patient.searcher.editPatient')"
                  class="far fa-edit"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-previous-cases-button"
                :disabled="selectedRows.length !== 1"
                @click="openPatientPreviousCases(selectedRows[0])"
              >
                <i
                  v-tooltip="$t('component.patient.searcher.goCases')"
                  class="far fa-history"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                v-if="$_canPrintPreviousCaseReport()"
                class="btn__icon btn__icon--primary px-2"
                qa="search-patient-modal-previous-cases-report-button"
                :disabled="selectedRows.length !== 1"
                @click="exportPatientPreviousCasesReportPDF(selectedRows[0])"
              >
                <i
                  v-tooltip="$t('component.modalPatientCases.previousCasesReport')"
                  class="far fa-file-export"
                  aria-hidden="true"
                />
              </b-button>
              <b-button
                id="patient-more"
                qa="search-patient-modal-ellipsis-button"
                class="btn__icon btn__icon--primary pl-2 pr-0"
                :disabled="selectedRows.length !== 1"
              >
                <i
                  class="fas fa-ellipsis-v"
                  aria-hidden="true"
                />
              </b-button>
              <b-popover
                v-if="selectedRows.length"
                target="patient-more"
                placement="bottom"
                triggers="click blur"
              >
                <div class="ap-popover">
                  <ul>
                    <li
                      v-if="$_canCreateIncident()"
                    >
                      <b-button
                        qa="search-patient-modal-incident-button"
                        @click="openIncidentFormModal(selectedRows[0])"
                      >
                        {{ $t('component.patient.searcher.incident') }}
                      </b-button>
                    </li>
                    <li
                      v-if="$_canAttachDocumentToPatient()"
                    >
                      <b-button
                        qa="search-patient-modal-attach-button"
                        :disabled="!$_canBeDocumentAttachedToPatient(selectedRows[0])"
                        @click="showAddFileModal(selectedRows[0])"
                      >
                        {{ $t('component.patient.searcher.attach') }}
                      </b-button>
                    </li>
                  </ul>
                </div>
              </b-popover>
            </div>
          </grid-header>
          <div
            v-if="rowData.length > 0"
            style="position:relative"
          >
            <patient-sidepanel
              v-if="showSidePanel && patientIdSelected"
              ref="patientSidePanel"
              :key="patientIdSelected"
              :patient-id="patientIdSelected"
              @closePatientSidepanel="closePatientSidePanel"
            />
            <vt-grid
              v-testing="'patients-grid'"
              class="ps-grid patient"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :row-data="rowData"
              master-detail
              use-detail-custom-config
              :detail-cell-renderer-params="detailCellRendererParams"
              :autosize-columns="false"
              pagination
              component-code="uicomponent.petitioner.patientgrid"
            />
          </div>
        </div>
      </div>
    </b-modal>
    <edit-patient-modal
      ref="editPatientModal"
      @edited="onPatientEdited($event)"
    />
    <modal-confirmation
      id="searchPatientConfirmationModal"
      ref="deleteConfirmationModal"
    />
    <create-patient-modal
      id="createPatientModal"
      ref="createPatientModal"
    />
    <patient-fusion-modal-component
      ref="modalPatientFusion"
      @patientsMerged="onPatientsMerged"
    />
    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree
        v-if="selectedPatientForAttachmentTreeModal"
        @incident-added="searchPatients()"
      />
    </modal-wrapper>
    <incident-form-modal
      v-if="coreEntity"
      id="incidenFormModal"
      ref="incidenFormModal"
      :core-entity="coreEntity"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="coreEntity"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <modal-wrapper ref="modalAssociateCaseWrapper">
      <modal-associated-cases-read-only
        ref="modalAssociateCase"
        :associated-cases="selectedCaseAssociatedCases"
        :patient-id="selectedCasePatientId"
        :case-number="selectedCaseCaseNumber"
      />
    </modal-wrapper>
    <common-attachment-modal-tree
      v-if="selectedPatientForAttachmentTreeModal"
      ref="attachmentTreeModal"
      :patient="selectedPatientForAttachmentTreeModal"
      :laboratory-id="laboratoryId"
      @attachments-updated="onAttachmentsUpdated"
    />
  </div>
</template>
<script>
import PatientSearcherComponent from '@/components/common/searcher/components/patient/patient-searcher.vue'
import PatientFusionModalComponent from '@/modules/RegistryModule/views/editCase/modals/patient-fusion/modal/patient-fusion-modal.vue'
import sessionStore from '@/session/sessionStore'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import GridHeader from '@/components/GridHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import patientResource from '@/api/resources/PatientResource'
import ModalConfirmation from '@/components/modals/ModalConfirmation'
import EditPatientModal from '@/components/patient/EditPatientModal'
import CreatePatientModal from '@/components/patient/CreatePatientModal'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import AddFileModal from '@/components/AddFileModal'
import { REQUEST_STATES } from '@/api/resources/entityState/request-state'
import caseServices from '@/api/resources/common/commonCaseServices'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'
import ModalAssociatedCasesReadOnly from '@/components/ModalAssociatedCasesReadOnly'
import i18n from '@/lang/i18n'
import PatientSidepanel from '@/components/searcherPatientModal/PatientSidepanel'
import PatientIdentifierResource from '@/api/resources/PatientIdentifierResource'
import CenterResource from '@/api/resources/common/commonCenterService'
import { PatientOperation } from '@/api/operations/models/patient-operation'
import { PatientOperationGuardMixin } from '@/api/operations/mixins/guards/patient-operation-guard.mixin'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import CellGridRichText from '@/components/common/CellGridRichText'
import CommonAttachmentModalTree from '@/components/attachments/CommonAttachmentModalTree'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import { AgGridColumnType, AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { SearcherStatus } from '@/components/common/searcher/shared/models/common-searcher.model'
import { HelperUtils } from '@/shared/util/helper-utils'

export default {
    name: 'SearchPatientModal',
    components: {
        PatientSearcherComponent,
        PatientFusionModalComponent,
        VtGrid,
        EditPatientModal,
        ModalConfirmation,
        CreatePatientModal,
        IncidentFormModal,
        AddFileModal,
        ModalAssociatedCasesReadOnly,
        PatientSidepanel,
        CommonAttachmentModalTree,
        CommonIncidentModalTree,
        GridHeader
    },
    mixins: [
        PatientOperationGuardMixin,
        CaseOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        idModal: { type: String, required: true }
    },
    data() {
        return {
            patientPreviousCasesWindowsReference: null,
            patientIdSelected: null,
            searchMessage: '',
            showSidePanel: false,
            patientInfo: null,
            isModalOpen: false,
            selectedRows: [],
            sessionState: sessionStore.state,
            loading: false,
            gridApi: null,
            rowData: [],
            coreEntity: 'PATIENT',
            detailCellRendererParams: {},
            selectedCaseAssociatedCases: [],
            selectedCaseCaseNumber: null,
            selectedCasePatientId: null,
            selected: null,
            columnDefs: [
                { type: AgGridColumnType.MASTER_DETAIL },
                { type: AgGridColumnType.CUSTOM_CHECKBOX },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patName',
                    headerName: i18n.tc('global.name'),
                    flex: 1
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patMiddleName',
                    headerName: i18n.t('grid.patMiddleName'),
                    flex: 1
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patSurname1',
                    headerName: i18n.t('global.surname1'),
                    flex: 1
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'patSurname2',
                    headerName: i18n.t('global.surname2'),
                    flex: 1
                },
                {
                    type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                    field: 'genderName',
                    headerName: i18n.tc('global.gender'),
                    flex: 1
                },
                {
                    type: AgGridColumnType.DATE,
                    field: 'patBirthdate',
                    headerName: i18n.t('global.birthday'),
                    flex: 1
                },
                {
                    type: 'text',
                    field: 'centerIdentifier',
                    headerName: 'NHC',
                    flex: 1
                }
            ],
            entitiesIdArray: [],
            selectedPatientForAttachmentTreeModal: null
        }
    },
    computed: {
        laboratoryId() {
            return this.$store.getters.labId
        },
        gridOptions() {
            return {
                pagination: false,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                onRowClicked: this.onRowClicked,
                getContextMenuItems: this.getContextMenuItems,
                floatingFilter: false,
                sideBar: gridSideBarConfig,
                onGridReady: this.onGridReady,
                onRowDataChanged: this.onRowDataChanged,
                onSelectionChanged: this.onSelectionChanged
            }
        },
        disabledModalPatientFusionButton() {
            return this.selectedRows.length > 2
        },

        noSearchDoneYet() {
            return this.searchStatus === SearcherStatus.NO_SEARCH_DONE_YET
        },
        tooManyResults() {
            return this.searchStatus === SearcherStatus.TOO_MANY_RESULTS
        }
    },
    methods: {
        async onPatientsMerged() {
            if (this.showSidePanel) {
                this.closePatientSidePanel()
            }
            await this.searchPatients()
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        onRowDataChanged({ api }) {
            if (this.rowData.length === 1) {
                const singleRow = this.rowData[0]
                const singleRowIdProp = 'patId'
                const singleRowId = singleRow.patId
                api.forEachNode(node => {
                    if (node.data[singleRowIdProp] === singleRowId) {
                        node.setSelected(true)
                    }
                })
            }
        },
        openIncidentsModalTree(clickedPatient) {
            this.selectedPatientForAttachmentTreeModal = { patId: clickedPatient.patId }
            this.$refs.incidentModalTreeWrapper.show([null, clickedPatient.patId])
        },
        async loadPatientSidePanelAttachments() {
            if (this.$refs.patientSidePanel) {
                await this.$refs.patientSidePanel.loadPatientAttachments()
            }
        },
        showAttachmentModal(clickedPatient) {
            this.selectedPatientForAttachmentTreeModal = clickedPatient
            this.$nextTick(() => {
                this.$refs.attachmentTreeModal.showPatient(clickedPatient.patId)
            })
        },
        async onAttachmentsAdded() {
            await this.searchPatients()
            await this.loadPatientSidePanelAttachments()
        },
        async onAttachmentsUpdated() {
            await this.searchPatients()
            await this.loadPatientSidePanelAttachments()
        },
        async onPatientEdited(patient) {
            await this.searchPatients()
            this.$emit('patientEdited', patient)
        },
        onRowClicked(params) {
            const isSame =
                this.patientIdSelected &&
                params.data.patId === this.patientIdSelected
            if (isSame) {
                this.showSidePanel = !this.showSidePanel
                return
            }
            this.patientIdSelected = params.data.patId
            this.showSidePanel = true
        },
        async loadAssociatedCase(casId) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(caseServices.getAssociatedCases(this.$store.getters.labId, casId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            return response
        },
        async showModalAssociatedCasesReadOnly(selectedCase) {
            this.selectedCaseCaseNumber = selectedCase.casenumber
            this.selectedCaseAssociatedCases = await this.loadAssociatedCase(selectedCase.id)
            if (this.selectedCaseAssociatedCases) {
                this.$refs.modalAssociateCaseWrapper.show()
            }
        },
        setDetailCellRendererParams() {
            this.detailCellRendererParams = {
                detailGridOptions: {
                    pagination: false,
                    rowModelType: AgGridRowModelType.CLIENT_SIDE,
                    floatingFilter: false,
                    sideBar: gridSideBarConfig,
                    getContextMenuItems: (params) => {
                        const clickedCase = params?.node.data
                        return [
                            {
                                name: this.$t('registry.btn.viewAnaReport'),
                                action: () => {
                                    const getCaseReportPDFParams = {
                                        laboratoryId: clickedCase.laboratory.labId,
                                        caseId: clickedCase?.id,
                                        caseLabel: clickedCase?.casenumber,
                                        print: false
                                    }
                                    HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
                                },
                                cssClasses: this.$_canPrintCaseReport() && this.$_canBeCaseReportPrinted(clickedCase) && clickedCase.status.code !== REQUEST_STATES.DRAFT.code ? '' : ['not-show']
                            },
                            {
                                name: this.$tc('global.showInGlobalLocator'),
                                action: () => this.showInGlobalLocator(clickedCase.casenumber)
                            }
                        ]
                    },
                    columnDefs: [
                        {
                            type: 'text',
                            field: 'casenumber',
                            headerName: this.$t('grid.casCasenumber')
                        },
                        {
                            type: AgGridColumnType.COMMON_CASE_STATUS,
                            field: 'status',
                            headerName: this.$tc('grid.detail.casStatusId')
                        },
                        {
                            type: 'associatedCases',
                            field: 'associatedCases',
                            headerName: this.$t('grid.detail.associatedCases'),
                            onCellClicked: params => {
                                this.showModalAssociatedCasesReadOnly(params?.node.data)
                            }
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'laboratory.labName',
                            headerName: this.$t('grid.detail.laboratory')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'physicianDepartment.name',
                            headerName: this.$t('grid.detail.requestingUnit')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'physicianPetitioner.fullName',
                            headerName: this.$t('grid.detail.requestingProfessional')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'pathologist.fullName',
                            headerName: this.$t('grid.detail.patName')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'notes',
                            headerName: this.$t('grid.detail.observations')
                        },
                        {
                            type: AgGridColumnType.DIACRITICS_INSENSITIVE_TEXT_FOR_CODE_DESCRIPTION_OR_STRING,
                            field: 'comprehensiveDiagnosis.text',
                            headerName: this.$t('grid.detail.comprehensiveDiagnosis'),
                            cellRendererFramework: CellGridRichText
                        },
                        {
                            type: 'text',
                            headerName: this.$t('registry.btn.consultation'),
                            headerTooltip: this.$t('registry.btn.consultation'),
                            field: 'grid.caseOfReference',
                            hide: false,
                            filter: false,
                            headerComponentParams: {
                                template: buildIconHeaderTemplate('far fa-glasses')
                            },
                            cellRenderer: (data) => {
                                if (data.value) {
                                    if (data.value === true) {
                                        return '<i class="success far fa-check" aria-hidden="true"/>'
                                    }
                                }
                                return ''
                            },
                            exportField: 'caseOfReference',
                            exportType: 'boolean'
                        },
                        {
                            type: ['dateTime', 'fixedWidth'],
                            headerName: i18n.t('grid.reqRequestDate'),
                            field: 'requestDate',
                            exportField: 'requestDate',
                            exportType: 'dateTime'
                        },
                        {
                            type: ['dateTime', 'fixedWidth'],
                            headerName: i18n.tc('grid.casRegistrationDate'),
                            field: 'registrationDate',
                            exportField: 'registrationDate',
                            exportType: 'dateTime'
                        },
                        {
                            type: ['dateTime', 'fixedWidth'],
                            headerName: i18n.tc('grid.casValidationDate'),
                            field: 'validationDate',
                            exportField: 'validationDate',
                            exportType: 'dateTime'
                        },
                        {
                            type: 'text',
                            headerName: i18n.t('global.externalReference'),
                            field: 'externalReference',
                            exportField: 'externalReference',
                            exportType: 'text'
                        },
                        {
                            type: 'text',
                            headerName: i18n.t('grid.headerName.requestLabel'),
                            field: 'requestLabel',
                            exportField: 'requestLabel',
                            exportType: 'text'
                        }
                    ]
                },
                getDetailRowData: async params => {
                    const data = await this.getMasterDetailRowData(params?.node.data.patId)
                    params.successCallback(data)
                }
            }
        },
        async getMasterDetailRowData(patId) {
            const [err, response] = await this.toCall(caseServices.getPreviousCases(this.laboratoryId, patId))
            if (err) {
                this.showErrorModalGlobal()
                return []
            }
            return response
        },
        showAddFileModal(patient) {
            this.entitiesIdArray = [patient.patId]
            this.$refs.addFileModalWrapper.show()
        },
        openIncidentFormModal(patient) {
            const entities = {
                patId: patient.patId
            }
            this.$refs.incidenFormModal.show(null, entities)
        },
        openModalPatientFusion(firstPatient, secondPatient) {
            this.$refs.modalPatientFusion.show(firstPatient?.patId, secondPatient?.patId)
        },
        openPatientPreviousCases(patient) {
            if (this.patientPreviousCasesWindowsReference) {
                this.patientPreviousCasesWindowsReference.close()
            }
            const routeData = this.$router.resolve({ name: 'patientCases', params: { patId: patient.patId, labId: this.$store.getters.labId } })
            this.patientPreviousCasesWindowsReference = window.open(routeData.href, '_blank')
            this.$emit('closePreviousCasesWindow')
        },
        showCreatePatientModal() {
            this.$refs.createPatientModal.show()
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        getContextMenuItems(params) {
            if (this.selectedRows.length === 2) {
                return [
                    {
                        name: this.$t('component.patient.searcher.patientMerge'),
                        action: () => this.openModalPatientFusion(this.selectedRows[0], this.selectedRows[1]),
                        cssClasses: !this.hasLabOperationGlobalMixin(this.laboratoryId, PatientOperation.MERGE) ? ['not-show'] : ''
                    }
                ]
            } else if (!this.selectedRows.length || this.selectedRows.length === 1) {
                const clickedPatient = params?.node.data
                return [
                    {
                        name: this.$t('component.patient.searcher.editPatient'),
                        action: () => this.showEditPatientModal(clickedPatient.patId),
                        cssClasses: this.$_canEditPatient() ? '' : ['not-show']
                    },
                    {
                        name: this.$tc('component.patient.searcher.deletePatient', 1),
                        action: () => this.deletePatient(clickedPatient.patId),
                        cssClasses: this.$_canDeletePatient() ? '' : ['not-show']
                    },
                    {
                        name: this.$t('component.patient.searcher.goCases'),
                        action: () => this.openPatientPreviousCases(clickedPatient)
                    },
                    {
                        name: this.$t('component.modalPatientCases.previousCasesReport'),
                        action: () => this.exportPatientPreviousCasesReportPDF(clickedPatient),
                        cssClasses: this.$_canPrintPreviousCaseReport() ? '' : ['not-show']
                    },
                    {
                        name: this.$t('registry.caseGrid.contextMenu.viewIncidents'),
                        action: () => this.openIncidentsModalTree(clickedPatient)
                    },
                    {
                        name: this.$t('component.patient.searcher.incident'),
                        action: () => this.openIncidentFormModal(clickedPatient),
                        cssClasses: this.$_canCreateIncident() ? '' : ['not-show']
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.viewAttach'),
                        action: () => this.showAttachmentModal(clickedPatient)
                    },
                    {
                        name: this.$t('component.patient.searcher.attach'),
                        action: () => this.showAddFileModal(clickedPatient),
                        cssClasses: this.$_canAttachDocumentToPatient() && this.$_canBeDocumentAttachedToPatient(clickedPatient) ? '' : ['not-show']
                    }
                ]
            } else {
                return []
            }
        },
        async showEditPatientModal(patId) {
            await this.$refs.editPatientModal.show(patId)
        },
        async deletePatient(patId) {
            this.$refs.deleteConfirmationModal.ask(async () => {
                this.updateLoading(true)
                let [err] = await this.toCall(patientResource.delete(patId))
                this.updateLoading(false)

                if (err) {
                    if (err.response && err.response.data && err.response.data.code) {
                        this.showErrorModalGlobal(err.response.data.message)
                    } else {
                        this.showErrorModalGlobal()
                    }
                    return
                }

                this.updateModalStateGlobal({
                    message: this.$tc('component.patient.searcher.patientDeleteSuccessful'),
                    icon: 'fal fa-check',
                    state: 'vt-modal vt-modal--success'
                })
                await this.searchPatients()
            })
        },
        async selectPatientAlt(patId) {
            const patientDetail = await this.loadPatient(patId)
            if (patientDetail) {
                this.$emit('selected', patientDetail)
                this.hide()
            }
        },
        async createCase(patId) {
            const patientDetail = await this.loadPatient(patId)
            if (patientDetail) {
                this.$emit('selectedNewCase', patientDetail)
                this.hide()
            }
        },
        async loadPatient(patId) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(patientResource.findById(patId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            return response
        },
        onGridReady({ api }) {
            this.gridApi = api
            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patMiddleName',
                    'patSurname2'
                )
            )
        },
        addFile() {
        },
        hide() {
            this.$refs.bModal.hide()
        },
        async addPatientIdentifiersColumns() {
            const [err, response] = await this.toCall(PatientIdentifierResource.getPersonIdentifiers())
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            const personIdentifiers = response
            if (personIdentifiers.length) {
                personIdentifiers.forEach((identifier) => {
                    this.columnDefs.push({
                        type: 'text',
                        field: identifier.code,
                        headerName: identifier.name,
                        flex: 1,
                        valueGetter: (params) => {
                            return params.data.personIdentifiers[identifier.code.toString()]?.identifier
                        }
                    })
                })
            }
        },
        async show() {
            this.setDetailCellRendererParams()
            await this.addPatientIdentifiersColumns()
            this.rowData = []
            this.isModalOpen = true
            this.$refs.bModal.show()
        },
        onHide() {
            if (this.patientPreviousCasesWindowsReference) {
                this.patientPreviousCasesWindowsReference.close()
            }
            this.$_cleanComponentData()
            this.$emit('onHide')
        },
        onOk() {
        },

        async getIdentifierTypes() {
            const response = await PromiseUtils.toCallSecure(PatientIdentifierResource.index())
            return response ?? []
        },
        async getCenters() {
            const response = await PromiseUtils.toCallSecure(CenterResource.getActiveCenters())
            return response ?? []
        },
        async searcher(searcherParams) {
            const { identifierType, center, id, name, middleName, surname1, surname2 } = searcherParams
            const params = {
                requestingLaboratoryId: this.$store.getters.labId,
                id,
                name,
                middleName,
                surname1,
                surname2,
                identifierType: identifierType?.code
            }
            if (identifierType?.idtScope === IdentifierTypeScope.CENTER_PATIENT) {
                params.centerCode = center?.code
            }
            const response = await patientResource.paginator(params)
            return response?.content ?? []
        },
        onSearched(patients) {
            this.rowData = patients

            if (this.showSidePanel) {
                this.closePatientSidePanel()
            }
            this.selectedRows = []
        },
        onSearchLoadingChanged(loading) {
            this.loading = loading
        },
        onSearchStatusChanged(status) {
            switch (status) {
            case SearcherStatus.ERROR:
                this.searchMessage = this.$tc('component.modalError.title')
                break
            case SearcherStatus.NO_RESULTS:
                this.searchMessage = this.$tc('component.patient.searcher.noResults')
                break
            case SearcherStatus.TOO_MANY_RESULTS:
                this.searchMessage = this.$tc('component.patient.searcher.tooManySearchResults')
                break
            case SearcherStatus.INVALID_IDENTIFIER:
                this.searchMessage = this.$tc('component.searcher.error.validIdentifier')
                break
            case SearcherStatus.SEARCH_CLEARED:
                this.searchMessage = ''
                break
            }
        },
        async searchPatients() {
            await this.$refs.patientSearcher?.search()
        },

        async exportPatientPreviousCasesReportPDF(patient) {
            const blob =
                await this.$_toCallUnique(
                    patientResource.getPreviousCaseAPReport(this.$store.getters.labId, patient.patId, false))
            if (blob) {
                const openFileParams = {
                    blob,
                    newWindowTabTitle: `- ${this.$tc('global.caseReport')}`
                }
                this.$_openFile(openFileParams)
            }
        },
        closePatientSidePanel() {
            this.showSidePanel = false
        }
    }
}
</script>
<style lang="scss">
.search-patient-modal .loading-state {
    width: 100%;
    height: calc(100vh - 400px);
    min-height: 425px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.search-patient-modal .patient-sidepanel {
    position: absolute;
    width: 900px;
    height: calc(100% - 32px);
    background: #FFF;
    z-index: 1;
    right: 0;
    box-shadow: -5px 0 5px -5px #b2b2b2;
    border: 1px solid vt-color(grid-general-border);
    display: flex;
    flex-direction: column;
    .patient-sidepanel__container {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        .patient-sidepanel__row {
            flex-grow: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
            .patient-sidepanel__main-col {
                min-height: 0;
                display: flex;
                flex-direction: column;
            }
        }
    }
}
.search-patient-modal .ag-layout-normal {
    padding: 0;
}
.search-patient-modal .patient-container .empty-search {
    height: calc(100vh - 400px);
    min-height: 350px;
}
.search-patient-modal .patient-container .btn__icon .btn__icon--primary {
    margin: 0 3px !important;
}
.search-patient-modal .modal-body {
    border-radius: .3rem;
    min-height: calc(100vh - 100px);
}
.search-patient-modal .create-patient-modal .modal-body,
.search-patient-modal .edit-patient-modal .modal-body,
.search-patient-modal .modalConfirmation .modal-body,
.search-patient-modal .incidentModal .modal-body {
    height: auto !important;
}
.search-patient-modal .patientFusionModal .modal-body {
    height: calc(100vh - 240px);
}
.search-patient-modal .create-patient-modal {
    padding: 0 !important;
}
/*.search-patient-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    font-size: font-size(very-small) !important;
}

.search-patient-modal .nav-pills {
    padding: 0 !important;
    background: vt-color(light-primary);
    height: 100%;
}

.search-patient-modal .tabs.row {
    height: calc(100vh - 360px) !important;
    border: 1px solid rgba(0, 0, 0, 0.125);
}

.search-patient-modal .nav-link.active {
    background: vt-color(white-light);
    color: vt-color(primary);
    position: relative;
}

.search-patient-modal .nav-link.active::before {
    content: "";
    position: absolute;
    width: 5px;
    height: 100%;
    background: vt-color(primary);
    left: 0;
}

.search-patient-modal .nav-link {
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    width: 70px;
    border-bottom: 2px solid vt-color(white-light);
}

.search-patient-modal .vt-tabs .nav-tabs .nav-link.active,
.vt-tabs--small .nav-tabs .nav-link.active {
    border-bottom: 2px solid vt-color(primary);
}

.search-patient-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link {
    border-bottom: 2px solid transparent;
}

.search-patient-modal .vt-tabs .nav-tabs .nav-link,
.vt-tabs--small .nav-tabs .nav-link i {
    font-size: font-size(very-normal) !important;
}

.search-patient-modal .techniques-tab .nav-link.active::before {
    background: transparent;
}
.search-patient-modal .section-indicator{
    position: absolute;
    width: auto;
    left: 23px;
    margin-top: 5px;
}
.search-patient-modal .nav-link {
    width: 50px;
}*/
</style>
<style lang="scss" scoped>
.title {
    font-size: font-size(normal);
    color: vt-color(primary);
}
.vertical-line {
    width: 1px;
    background: #dee2e6;
    display: inline-block;
    margin: 0 15px;
    height: 20px;
}
.ps-filter-form {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    max-width: 1200px;
    margin-bottom: 24px;
}
.ps-grid {
    height: 450px;
}
.search-patient-modal .ag-status-bar {
    display: none;
}
small {
    color: grey;
}
.error-msg {
    font-size: 1.1rem;
    padding-top: 1rem;
    color: #fc6274;
}
</style>
<style lang="scss">
.search-patient-modal .form-group {
    margin-bottom: 0;
}
@media (max-width: 1300px) {
    .search-patient-modal .patient-sidepanel {
        width: 650px;
    }
}
.search-patient-modal .page_renderer {
    padding: 0 5px !important;
    height: calc(100% - 30px) !important;
    overflow-y: auto !important;
    margin: 10px !important;
}
.search-patient-modal .page_renderer .vt-grid.ag-theme-balham.ps-grid {
    height: auto !important;
}
.search-patient-modal .ps-grid {
    height: calc(100vh - 265px) !important;
    min-height: 425px;
}
.ps-grid.patient .ag-row.ag-row-no-focus.ag-row-odd.ag-row-level-1.ag-full-width-row.ag-row-position-absolute {
    background-color: #FFF !important;
}

</style>
